import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ColDef, GridApi, GridOptions, ValueFormatterParams } from "ag-grid-community";
import { CxoneEtlDataImportService } from "../../cxone-etl-data-imports.service";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { ListViewObj, ViewDataObj } from "./cxone-custom-data-import-view-model";
import { ToastrManager } from "@niceltd/cxone-components/toastr";
import { CustomNoRowsOverlayComponent } from "../../../shared/custom-no-rows-overlay/custom-no-rows-overlay.component";
import { AgGridHelperService } from "src/app/reusables/services/ag-grid-helper.service";
import { CxoneHighlightSearchCellRendererComponent } from "src/app/reusables/ag-grid-components/cxone-highlight-search-cell-renderer/cxone-highlight-search-cell-renderer.component";
import { PERMISSION } from "../../cxone-etl-data-imports-model";

@Component({
    selector: "cv-cxone-custom-data-import-view",
    templateUrl: "./cxone-custom-data-import-view.component.html",
    styleUrls: ["./cxone-custom-data-import-view.component.scss"],
})
export class CxoneCustomDataImportViewComponent implements OnInit {
    @Input() importViewData = null;
    @Input() rawDataDefinitions = null;
    @Output() backButtonClick = new EventEmitter();
    public userCountLabel = "";
    public selectedItemCountLabel: any;
    public btnDisabled = true;
    public feedName = "";
    public companyID = "";
    public importID = "";
    public filteredUserGridData: ViewDataObj[]; // This is the actual filtered data which is to be displayed;
    public rawData: ViewDataObj[];
    public columnNames: ColDef[];
    public searchPlaceholderText = "";
    public searchInput = '';
    public showDeleteBtn = false;
    public gridApi: GridApi;
    public showSpinner = false;
    public gridOptions: GridOptions = {
        onGridReady: (param) => this.gridApi = param.api,
        onRowSelected: () => this.onRowSelected(),
        onRowDataUpdated: () => this.setRowCount(),
        onFilterChanged: () => {
            this.gridApi.redrawRows();
            this.setRowCount();
        },
        components: {
            CustomNoRowsOverlayComponent,
            cxoneHighlightSearchRenderer: CxoneHighlightSearchCellRendererComponent
        },
        noRowsOverlayComponent: "CustomNoRowsOverlayComponent",
        noRowsOverlayComponentParams: {
            noRowsOverlayTextFunc: () =>
                this.searchInput !== ""
                    ? this.getLocalizationString(
                        "No_Result_Found",
                        "No result found"
                    )
                    : this.getLocalizationString(
                        "No_Feed_Added",
                        "No feed added yet"
                    ),
            noRowsOverlayIconPathFunc: () => {
                const path =
                    this.searchInput !== ""
                        ? "../App_Themes/CXone/icons/no-results.svg#no-results"
                        : "../App_Themes/CXone/icons/empty-state.svg#empty-state";
                return path;
            },
        },
        context: this,
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: (node) => {
            const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
            return result;
        },
        getRowId: ({ data: rowData }) => rowData._id,
        scrollbarWidth: 8
    };

    constructor(
        private cxoneEtlDataImportService: CxoneEtlDataImportService,
        private cvLocalizationService: CVLocalizationService,
        private toasterManager: ToastrManager,
        private agGridHelperService: AgGridHelperService
    ) { }

    async ngOnInit(): Promise<void> {
        await this.loadLocalization();
        this.importID = this.importViewData.ImportID;
        this.feedName = this.importViewData.FeedName;
        this.companyID = this.importViewData.CompanyID;
        this.setselectedItemCountLabel(0);
        this.columnNames = this.getColumnNames();
        await this.loadData(this.importID);
        const permission = await this.cxoneEtlDataImportService.getPermissions("CXOneCustomDataImport") || [];
        this.showDeleteBtn = permission.includes(PERMISSION.CREATE);
    }

    async loadData(importID: string) {
        this.showSpinner = true;
        try {
            const responseData: ListViewObj[] = await this.cxoneEtlDataImportService.loadViewData(importID);
            const resultData: ViewDataObj[] = [];
            responseData.forEach((res: ViewDataObj) => {
                res.ETLData._id = res._id;
                resultData.push(res.ETLData);
            });
            this.rawData = [...responseData];
            this.filteredUserGridData = [...resultData];
        }
        catch (error) {
            this.filteredUserGridData = [];
            this.rawData = [];
            this.toasterManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
        finally {
            this.showSpinner = false;
        }
    }

    onRowSelected() {
        const selectedRows = this.gridApi.getSelectedRows();
        this.btnDisabled = !selectedRows.length;
        this.setselectedItemCountLabel(selectedRows.length);
    }

    getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("CXone_Custom_Data_Import", key, defaultValue);
        return baseString;
    }

    userSearchQueryChange(inputText: string) {
        this.searchInput = inputText;
        this.gridApi.setGridOption('quickFilterText', inputText);
        if (this.gridApi?.getDisplayedRowCount() === 0) {
            this.gridApi.showNoRowsOverlay();
        }
        else {
            this.gridApi?.hideOverlay();
        }
    }

    getColumnNames(): ColDef[] {
        const searchKey = `${this.companyID}|${this.importViewData.SourceSystem}|${this.feedName}|RawDataDefinition`;
        const colObj = this.rawDataDefinitions.filter((rawDataObj: ViewDataObj) => rawDataObj._id === searchKey);
        const columnsDef = [];
        if (colObj.length > 0) {
            const fieldMapping = colObj[0].FieldTypeMapping;
            Object.keys(fieldMapping).sort().forEach((key: string, index: number) => {
                const colDefObj: ColDef = {};
                colDefObj.headerName = key;
                colDefObj.cellRenderer = 'cxoneHighlightSearchRenderer';
                colDefObj.field = key;
                colDefObj.suppressMovable = true;
                colDefObj.minWidth = 150;
                colDefObj.valueFormatter = (params: ValueFormatterParams) => params.data[key]?.toString();
                if (index === 0) {
                    colDefObj.headerCheckboxSelection = true;
                    colDefObj.checkboxSelection = true;
                    colDefObj.headerClass = 'checkBoxClass'
                }
                columnsDef.push(colDefObj);
            });
        }
        return columnsDef;
    }

    handleBackBtnClick() {
        this.backButtonClick.emit(false);
    }

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('CXone_Custom_Data_Import');
        }
        catch {
            console.log('Failed to load localization for CXone_Custom_Data_Import');
        }
        finally {
            this.searchPlaceholderText = this.getLocalizationString("Search_Data_Feeds", "Search data feeds");
        }
    }

    setselectedItemCountLabel(count: number) {
        this.selectedItemCountLabel = `${count} ${this.getLocalizationString("ETL_Selected", "selected")}`;
    }

    setRowCount() {
        this.userCountLabel = `${(this.gridApi.getDisplayedRowCount())} ${this.getLocalizationString("Results", "results")}`;
    }

    async deleteBtnClick() {
        this.showSpinner = true;
        const payload = [];
        const selectedRows = this.gridApi.getSelectedRows();
        selectedRows.forEach(selected => {
            const item = this.rawData.find(row => row._id === selected._id);
            if (item) {
                payload.push(item);
            }
        });
        try {
            await this.cxoneEtlDataImportService.deleteSelectedViewitems(payload);
            this.toasterManager.showToaster('success', this.getLocalizationString("Data_Import_Deleted_Msg", "Data deleted successfully."));
        }
        catch (error) {
            this.toasterManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
        finally {
            this.filteredUserGridData = this.filteredUserGridData.filter(item => !payload.
                some(payloadItem => payloadItem._id === item._id));
            this.setselectedItemCountLabel(0);
        }
        this.showSpinner = false;
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }
}
